import React, { useEffect, useState } from "react";
import axios from "axios";

import logo from "../../../assets/images/wa-horizontal-color.png";
import "../datos/datos.css";

/*
	"Usar el celular" (/foto/<token>, Esteban 2026-10-19): la asesora escanea el QR que le enseña la PC y aquí toma la foto
	de la identificación o del comprobante. Sin sesión de WAPP: el enlace sirve 10 minutos. Al subirla, la PC la recibe al
	instante. Mismo look que el formulario del cliente.
*/

const API = (process.env.REACT_APP_API_URL_V2 || "") + "/captura/";

/** La foto del celular pesa 4-8 MB: se baja a 1600 px en JPEG (HEIC del iPhone incluido, si el navegador la sabe leer). */
const achicar = (archivo: File): Promise<Blob> => new Promise((resolve) => {
	if (!archivo.type.startsWith("image/") && !/\.hei[cf]$/i.test(archivo.name)) { resolve(archivo); return; }
	const url = URL.createObjectURL(archivo);
	const img = new Image();
	img.onload = () => {
		const escala = Math.min(1, 1600 / Math.max(img.width, img.height));
		const c = document.createElement("canvas");
		c.width = Math.round(img.width * escala); c.height = Math.round(img.height * escala);
		c.getContext("2d")?.drawImage(img, 0, 0, c.width, c.height);
		URL.revokeObjectURL(url);
		c.toBlob((b) => resolve(b && (b.size < archivo.size || /hei[cf]/i.test(archivo.type + archivo.name)) ? b : archivo), "image/jpeg", 0.85);
	};
	img.onerror = () => { URL.revokeObjectURL(url); resolve(archivo); };
	img.src = url;
});

const mensaje = (e: any, porDefecto: string): string => {
	const m = e?.response?.data?.message;
	return typeof m === "string" && m.trim() !== "" && !/server error|not found/i.test(m) ? m : porDefecto;
};

const Foto: React.FC<{ token: string }> = ({ token }) => {
	const [info, setInfo] = useState<{ titulo: string; tipo: string } | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [aviso, setAviso] = useState<string | null>(null);
	const [subiendo, setSubiendo] = useState<boolean>(false);
	const [lista, setLista] = useState<boolean>(false);
	const [vista, setVista] = useState<string | null>(null);

	useEffect(() => {
		axios.get(API + token).then((r) => setInfo(r.data))
			.catch((e) => setError(mensaje(e, "No se pudo abrir el enlace. Revisa tu conexión e intenta de nuevo.")));
	}, [token]);

	const subir = async (archivo?: File) => {
		if (!archivo) return;
		setSubiendo(true);
		setAviso(null);
		try {
			const blob = await achicar(archivo);
			if (blob.type.startsWith("image/")) setVista(URL.createObjectURL(blob));
			const f = new FormData();
			f.append("archivo", blob, blob === archivo ? archivo.name : "foto.jpg");
			await axios.post(API + token, f);
			setLista(true);
		} catch (e) {
			setAviso(mensaje(e, "No se pudo subir la foto. Intenta de nuevo."));
		} finally {
			setSubiendo(false);
		}
	};

	const cabeza = <div className="dcCabeza"><img src={logo} alt="Washington Academy" className="dcLogo" /></div>;
	if (error) return <div className="dc">{cabeza}<div className="dcCentro"><p className="dcError">{error}</p></div></div>;
	if (!info) return <div className="dc"><div className="dcCentro"><span className="dcGiro grande" /></div></div>;

	const esId = info.tipo !== "comprobante";
	return (
		<div className="dc">
			{cabeza}
			<div className="dcCuerpo">
				<div className="dcPregunta" key={lista ? "lista" : "foto"}>
					{lista ? (
						<div className="dcListo">
							<div className="dcPalomita" aria-hidden="true">✓</div>
							<h1 className="dcTitulo grande">Listo</h1>
							<p className="dcAyuda">La foto ya va para la computadora. Puedes cerrar esta página.</p>
							{vista ? <img src={vista} alt="" style={{ maxWidth: "100%", maxHeight: 220, borderRadius: 12, marginTop: 8 }} /> : null}
							<p className="dcNota">Si salió borrosa, tómala de nuevo: reemplaza a la anterior mientras la computadora no la haya tomado.</p>
						</div>
					) : (
						<>
							<h1 className="dcTitulo">{info.titulo}</h1>
							<p className="dcAyuda">{esId ? "Del frente, donde están el nombre y la foto. Con buena luz, sin reflejos y sin cortar las orillas." : "Que se lean el monto, la fecha y la referencia."}</p>
							<label className="dcFoto">
								<input type="file" accept="image/*" capture="environment" onChange={(e) => { subir(e.target.files?.[0]); e.target.value = ""; }} />
								<span className="dcFotoIcono" aria-hidden="true">{subiendo ? <span className="dcGiro" /> : "📷"}</span>
								<span className="dcFotoTexto"><b>{subiendo ? "Subiendo…" : "Tomar foto"}</b><small>Se abre la cámara del celular</small></span>
							</label>
							<label className="dcFoto dcEspacio">
								<input type="file" accept="image/*,application/pdf" onChange={(e) => { subir(e.target.files?.[0]); e.target.value = ""; }} />
								<span className="dcFotoIcono" aria-hidden="true">+</span>
								<span className="dcFotoTexto"><b>Elegir de la galería</b><small>Una foto o un PDF que ya tengas</small></span>
							</label>
						</>
					)}
				</div>
			</div>
			{aviso ? <div className="dcPie"><div className="dcAviso" role="alert">{aviso}</div></div> : null}
			{lista ? (
				<div className="dcPie">
					<div className="dcBotones">
						<label className="dcSeguir" style={{ display: "grid", placeItems: "center", cursor: "pointer" }}>
							Tomar otra
							<input type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={(e) => { setLista(false); subir(e.target.files?.[0]); e.target.value = ""; }} />
						</label>
					</div>
				</div>
			) : null}
		</div>
	);
};

export default Foto;
